<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&hellip;</div>

    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Listas de precios</h1>
          <p class="view-header__subtitle">Listas que aplican incrementos sobre la lista CONTADO o sobre otra lista vinculada.</p>
        </div>
        <div class="view-header__actions">
          <button type="button" class="btn view-header__btn view-header__btn--primary" @click="openNewPriceList">
            <i class="bi bi-plus-lg"></i>
            Nueva lista
          </button>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="doSearch" type="search" class="form-control" placeholder="Buscar lista...">
          </div>
        </div>
      </div>
    </div>

    <!-- TABLA -->
    <div class="data-table">
      <vdtnet-table
        ref="table"
        :fields="fields"
        :opts="opts"
        :selectCheckbox="0"
        container-class-name="table-responsive"
        class-name="table mb-0"
        @edit="openEditPriceList"
        @delete="deletePriceList"
      />
    </div>

    <!-- MODAL NUEVA / EDITAR LISTA -->
    <transition name="modal">
      <div v-if="showPriceListModal" class="modal-mask-product">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="form-card__header">
              <h6 class="form-card__title">
                <i :class="priceList.id == null ? 'bi bi-plus-lg' : 'bi bi-pencil'"></i>
                {{ priceList.id == null ? 'Nueva lista de precios' : 'Editar lista de precios' }}
              </h6>
              <button type="button" class="form-card__btn" @click="closePriceListModal">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>

            <div class="modal-body form-card form-card--bare">
              <div class="form-card__body">
                <div class="form-card__field">
                  <label class="form-card__label" for="pl-name">Nombre</label>
                  <input
                    id="pl-name"
                    v-model.trim="priceList.name"
                    type="text"
                    class="form-control"
                    placeholder="Nombre de la lista"
                    :class="{ 'is-invalid': nameState === false }"
                  >
                  <div v-if="nameState === false" class="form-card__error">El nombre debe tener al menos 3 letras.</div>
                  <div v-else class="form-card__hint">Campo obligatorio.</div>
                </div>

                <div class="form-card__field">
                  <label class="form-card__label" for="pl-currency">Moneda</label>
                  <q-select v-model="priceList.currency" :options="currencyOptions" placeholder="- Seleccionar moneda -" />
                  <div class="form-card__hint">Campo obligatorio.</div>
                </div>

                <div class="form-card__field">
                  <label class="form-card__label" for="pl-linked">Lista vinculada</label>
                  <q-select v-model="priceList.price_list_id" :options="linkablePriceListOptions" placeholder="Ninguna" />
                  <div class="form-card__hint">Si se vincula a otra lista, los precios se calculan aplicando el incremento sobre esa lista.</div>
                </div>

                <div class="form-card__field">
                  <label class="form-card__label" for="pl-increment">% de incremento</label>
                  <input
                    id="pl-increment"
                    v-model="priceList.increment"
                    type="number"
                    step="0.01"
                    class="form-control"
                    placeholder="0.00"
                  >
                </div>
              </div>
            </div>

            <div class="form-card__footer">
              <button type="button" class="form-card__btn" @click="closePriceListModal">Cancelar</button>
              <button
                type="button"
                class="form-card__btn form-card__btn--primary"
                :disabled="nameState === false || !priceList.name"
                @click="savePriceList"
              >
                <i class="bi bi-check-lg"></i>
                Guardar lista
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import VdtnetTable from '@/components/global/VdtnetTable.vue';
import QSelect from '@/components/shared/QSelect.vue';

export default {
  components: {
    VdtnetTable,
    QSelect,
  },
  data: function () {
    return {
      doSearch: '',
      isLoading: false,
      showPriceListModal: false,
      priceList: {
        id: null,
        name: "",
        price_list_id: null,
        increment: "",
        currency: "",
      },
      emptyPriceList: {
        id: null,
        name: "",
        price_list_id: null,
        increment: "",
        currency: "",
      },
      optionsCurrency: [
        { value: "$", text: "Pesos" },
        { value: "US$", text: "Dólares" },
      ],
      opts: {
        language: {
          url: '//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json',
        },
        ajax: {
          url: process.env.VUE_APP_BASE_URL + "/api/priceslist/list",
          type: "GET",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        },
        autoWidth: false,
        pageLength: 15,
        searching: true,
      },
      fields: {
        id: { visible: false },
        name: {
          label: "Lista de precios",
          searchable: true,
          render: (data, type) =>
            type === 'display' ? `<span class="data-table__primary">${data}</span>` : data,
        },
        price_list_id: { label: "Lista vinculada" },
        increment: {
          label: "% de incremento",
          align: "right",
          render: (data, type) => {
            if (type !== 'display') return data;
            if (data === null || data === '' || data === undefined) return '';
            return `${data}%`;
          },
        },
        actions: {
          isLocal: true,
          label: "Acciones",
          width: "100px",
          align: "right",
          orderable: false,
          searchable: false,
          render: (_data, _type, row) => {
            if (row.name === 'CONTADO') return '';
            return `
              <div class="table-action-group">
                <button data-action="edit" class="table-action-btn" type="button" title="Editar">
                  <i class="bi bi-pencil"></i>
                </button>
                <button data-action="delete" class="table-action-btn" type="button" title="Borrar">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            `;
          },
        },
      },
    };
  },
  computed: {
    ...mapState('priceLists', ['priceLists']),
    currencyOptions() {
      return this.optionsCurrency.map(opt => ({ value: opt.value, label: opt.text }));
    },
    linkablePriceListOptions() {
      return this.linkablePriceLists.map(pl => ({ value: pl.id, label: pl.name }));
    },
    nameState() {
      const len = (this.priceList.name || '').length;
      return len < 3 && len > 0 ? false : null;
    },
    linkablePriceLists() {
      const list = Array.isArray(this.priceLists) ? this.priceLists : [];
      return list.filter((pl) => pl.id !== this.priceList.id);
    },
  },
  watch: {
    doSearch(newValue, oldValue) {
      if (newValue.length >= 4 || oldValue.length >= 4) {
        this.$refs.table.search(this.doSearch);
      }
      if (newValue === '' && oldValue.length > 0) {
        this.$refs.table.search(this.doSearch);
      }
    },
  },
  mounted() {
    this.$store.dispatch('priceLists/getPriceLists');
    this.$nextTick(() => {
      const dt = this.$refs.table && this.$refs.table.dataTable;
      if (!dt) return;
      dt.on('preXhr.dt', () => { this.isLoading = true; });
      dt.on('xhr.dt', () => { this.isLoading = false; });
    });
  },
  methods: {
    refreshTable() {
      if (this.$refs.table !== undefined) this.$refs.table.reload();
      this.$store.dispatch('priceLists/getPriceLists');
    },
    openNewPriceList() {
      this.priceList = Object.assign({}, this.emptyPriceList);
      this.showPriceListModal = true;
    },
    openEditPriceList(data) {
      this.isLoading = true;
      this.$http.get("/api/priceslist/getPriceList/" + data.id).then((response) => {
        this.priceList = response.data.data;
        this.isLoading = false;
        this.showPriceListModal = true;
      });
    },
    closePriceListModal() {
      this.showPriceListModal = false;
      this.priceList = Object.assign({}, this.emptyPriceList);
    },
    validateForm() {
      if (!this.priceList.name || this.priceList.name.trim() === "") {
        alert("Debe ingresar un nombre");
        return false;
      }
      if (this.priceList.name.length < 3) {
        alert("El nombre debe tener al menos 3 letras");
        return false;
      }
      if (!this.priceList.currency) {
        alert("Debe seleccionar una moneda");
        return false;
      }
      return true;
    },
    savePriceList() {
      if (!this.validateForm()) return;
      this.isLoading = true;
      const isEdit = this.priceList.id != null;
      const url = isEdit
        ? "/api/priceslist/edit/" + this.priceList.id
        : "/api/priceslist/add";
      this.$http.post(url, this.priceList).then(() => {
        this.closePriceListModal();
        this.refreshTable();
      }).finally(() => {
        this.isLoading = false;
      });
    },
    deletePriceList(data) {
      if (!confirm("¿Está seguro que desea borrar esta lista de precios?")) return;
      this.isLoading = true;
      this.$http.post("/api/priceslist/delete", { id: data.id }).then((response) => {
        if (response.data.code == 201) {
          this.refreshTable();
        }
      }).finally(() => {
        this.isLoading = false;
      });
    },
  },
};
</script>

<style scoped>
.modal-mask-product {
  position: fixed;
  z-index: 1050;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(16, 24, 40, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
}

.modal-dialog {
  margin: 0;
  width: 100%;
  max-width: 560px;
}

.modal-content {
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.18);
  overflow: hidden;
  background: var(--surface);
}

.modal-body {
  max-height: 80vh;
  overflow-y: auto;
  padding: 0;
}

.modal-enter,
.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-content,
.modal-leave-active .modal-content {
  transform: scale(1.02);
}
</style>
